import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link, Paper, Tooltip as MuiTooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { isMobile } from "is-mobile";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Page } from "../../../../components/page-base";
import { AppDataGrid } from "../../../../components/datagrid/AppDataGrid";
import { NameCell } from "../../../../components/datagrid/NameCell";
import { iniciais } from "../../../../components/datagrid/iniciais";
import { ChipButton, ChipGroupLabel, ToolbarDivider } from "../../../../components/datagrid/ChipButton";
import type { DataGridColumn, DataGridRow } from "../../../../components/datagrid/types";
import { KpiStrip } from "../../../../components/kpi-strip";
import { formatarNumero } from "../../../../components/kpi-strip/formatar";
import { USER_TYPE_LABEL } from "../../../../components/page-base/admin-nav/items";
import useUserStore from "../../../../store/user";
import { Container } from "../../../sellers/pages/styles";
import { ActivityLogsService, type Online, type Painel } from "../../services";
import { cidadeDoLog, descreverAparelho } from "../aparelho";
import { aparelhosAgrupados, duracao, haQuanto, matrizDeCalor } from "./calculos";
import { MapaAgora } from "./mapa-agora";

const PERIODOS = [7, 30, 90];
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const LARGURA = "calc(100% - 64px)";

const tomDoPapel = (role?: string | null) =>
  role === "ADMIN" ? "red" : role === "STAFF" ? "green" : role === "CUSTOMER" ? "grey" : "brand";

const Cartao = ({ titulo, subtitulo, children }: { titulo: string; subtitulo?: string; children: React.ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0, boxSizing: "border-box" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
      {titulo}
    </Typography>
    {subtitulo && (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {subtitulo}
      </Typography>
    )}
    {children}
  </Paper>
);

const Lista = ({ linhas }: { linhas: { chave: string; principal: string; secundario?: string; valor: string }[] }) =>
  linhas.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      Sem dados no período.
    </Typography>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {linhas.map((l) => (
        <Box
          key={l.chave}
          sx={{ display: "flex", gap: 1, justifyContent: "space-between", py: 0.75, borderTop: "1px solid #F1F3F4" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {l.principal}
            </Typography>
            {l.secundario && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-word" }}>
                {l.secundario}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
            {l.valor}
          </Typography>
        </Box>
      ))}
    </Box>
  );

export const UsoDaEquipe = () => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [painel, setPainel] = useState<Painel | null>(null);
  const [online, setOnline] = useState<Online | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [pagina, setPagina] = useState({ page: 1, pageSize: 20 });

  const carregarOnline = useCallback(async () => {
    if (!user?.token) return;
    setOnline(await ActivityLogsService.online(user.token));
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) return;
    let vivo = true;
    setCarregando(true);
    ActivityLogsService.painel(dias, user.token).then((p) => {
      if (!vivo) return;
      setPainel(p);
      setCarregando(false);
    });
    return () => {
      vivo = false;
    };
  }, [dias, user?.token]);

  // "Agora" atualiza sozinho a cada 30 s
  useEffect(() => {
    carregarOnline();
    const id = window.setInterval(carregarOnline, 30_000);
    return () => window.clearInterval(id);
  }, [carregarOnline]);

  const k = painel?.kpis;
  const pessoas = online?.pessoas ?? [];

  const porDia = useMemo(
    () =>
      (painel?.porDia ?? []).map((d) => ({
        dia: d.dia.slice(8, 10) + "/" + d.dia.slice(5, 7),
        usuarios: Number(d.usuarios),
        alteracoes: Number(d.alteracoes),
        telas: Number(d.telas),
        negados: Number(d.negados),
        horas: Math.round((Number(d.segundos) / 3600) * 10) / 10,
      })),
    [painel?.porDia],
  );

  const calor = useMemo(() => matrizDeCalor(painel?.porHora ?? []), [painel?.porHora]);

  const abrirLogs = useCallback(
    (userId: number, nome: string | null) =>
      navigate(`/logs-de-acesso?userId=${userId}&nome=${encodeURIComponent(nome ?? "")}&tipo=&dias=${dias}`),
    [navigate, dias],
  );

  const colunas: DataGridColumn[] = [
    { id: "usuario", header: "Usuário", minWidth: 240 },
    { id: "tempo", header: "Tempo de uso", minWidth: 120 },
    { id: "dias", header: "Dias ativos", minWidth: 90, hideOnMobile: true },
    { id: "alteracoes", header: "Alterações", minWidth: 100 },
    { id: "telas", header: "Telas abertas", minWidth: 100, hideOnMobile: true },
    { id: "redes", header: "Redes · aparelhos", minWidth: 130 },
    { id: "cidades", header: "Cidades (IP)", minWidth: 200, hideOnMobile: true },
    { id: "ultimo", header: "Último", minWidth: 100 },
  ];

  const linhas: DataGridRow[] = useMemo(() => {
    const todos = painel?.usuarios ?? [];
    const ini = (pagina.page - 1) * pagina.pageSize;
    return todos.slice(ini, ini + pagina.pageSize).map((u) => {
      const papel = u.userRole ? USER_TYPE_LABEL[u.userRole] ?? u.userRole : "";
      const ips = Number(u.ips);
      return {
        id: u.userId,
        render: {
          usuario: (
            <NameCell
              primaryText={u.userName || u.userEmail || `#${u.userId}`}
              secondaryText={[u.userEmail, papel].filter(Boolean).join(" · ")}
              avatar={iniciais(u.userName || u.userEmail || "?")}
              avatarTone={tomDoPapel(u.userRole)}
              onClick={() => abrirLogs(u.userId, u.userName || u.userEmail)}
            />
          ),
          tempo: <Typography variant="body2">{duracao(u.segundos)}</Typography>,
          dias: <Typography variant="body2">{formatarNumero(Number(u.diasAtivos))}</Typography>,
          alteracoes: <Typography variant="body2">{formatarNumero(Number(u.alteracoes))}</Typography>,
          telas: <Typography variant="body2">{formatarNumero(Number(u.telas))}</Typography>,
          redes: (
            <Typography variant="body2" sx={{ color: ips >= 3 ? "#C5221F" : undefined, fontWeight: ips >= 3 ? 700 : 400 }}>
              {ips} · {formatarNumero(Number(u.aparelhos))}
              {Number(u.negados) > 0 && (
                <Typography component="span" variant="caption" sx={{ display: "block", color: "#C5221F" }}>
                  {formatarNumero(Number(u.negados))} login(s) negado(s)
                </Typography>
              )}
            </Typography>
          ),
          cidades: (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
              {u.cidades || "—"}
            </Typography>
          ),
          ultimo: <Typography variant="body2">{haQuanto(u.ultimo)}</Typography>,
        },
      };
    });
  }, [painel?.usuarios, pagina, abrirLogs]);

  const comLocalizacao = pessoas.filter((p) => p.lat != null).length;

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Uso da equipe"
          subtitle="Quem está no painel agora, de onde, e como a equipe usou o sistema no período. Clique numa pessoa para ver tudo o que ela fez."
        />

        {/* -------- Agora -------- */}
        <Box sx={{ width: LARGURA, mt: 3 }}>
          <Cartao
            titulo={`Agora no painel: ${online?.online ?? 0} pessoa(s)`}
            subtitulo={`Atualiza a cada 30 s. Ponto verde = posição do aparelho (${comLocalizacao} com localização); azul tracejado = cidade pelo IP; cinza = saiu nos últimos 30 min.`}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) minmax(0, 2fr)" },
                gap: 2,
                mt: 1,
              }}
            >
              <Box sx={{ height: { xs: 280, md: 380 } }}>
                <MapaAgora pessoas={pessoas} />
              </Box>
              <Box sx={{ maxHeight: { xs: "none", md: 380 }, overflowY: "auto" }}>
                {pessoas.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Ninguém usou o painel na última meia hora.
                  </Typography>
                )}
                {pessoas.map((p) => (
                  <Box
                    key={p.userId}
                    sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", py: 1, borderTop: "1px solid #F1F3F4" }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        mt: 0.75,
                        flex: "0 0 auto",
                        bgcolor: p.online ? "#34A853" : "#BDC1C6",
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        variant="body2"
                        sx={{ fontWeight: 600, textAlign: "left" }}
                        onClick={() => abrirLogs(p.userId, p.userName || p.userEmail)}
                      >
                        {p.userName || p.userEmail}
                      </Link>
                      <Typography variant="caption" sx={{ display: "block", color: "#3c4043" }}>
                        {p.screen ?? "—"} · {p.online ? `online desde ${haQuanto(p.inicio).replace("há ", "")}` : `saiu ${haQuanto(p.ultimo)}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {cidadeDoLog(p) ?? "cidade desconhecida"} · {descreverAparelho(p.userAgent)}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block" }}>
                        {p.lat != null && p.lon != null ? (
                          <Link href={`https://www.google.com/maps?q=${p.lat},${p.lon}`} target="_blank" rel="noopener noreferrer">
                            posição exata{p.accuracyM != null ? ` (±${p.accuracyM} m)` : ""}
                          </Link>
                        ) : p.geoStatus === "denied" ? (
                          <span style={{ color: "#C5221F" }}>não permitiu a localização</span>
                        ) : p.userRole === "CUSTOMER" ? (
                          <span style={{ color: "#5f6368" }}>cliente: localização não é pedida</span>
                        ) : (
                          <span style={{ color: "#5f6368" }}>sem posição do aparelho</span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Cartao>
        </Box>

        {/* -------- Período -------- */}
        <Box sx={{ width: LARGURA, mt: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <ChipGroupLabel>Período</ChipGroupLabel>
          {PERIODOS.map((p) => (
            <ChipButton key={p} type="button" $on={dias === p} aria-pressed={dias === p} onClick={() => setDias(p)}>
              {p} dias
            </ChipButton>
          ))}
          <ToolbarDivider />
          <Typography variant="caption" color="text.secondary">
            Tempo de uso conta só com a aba aberta e visível. O registro começou em 01/10/2026.
          </Typography>
        </Box>

        <Box sx={{ width: LARGURA, mt: 2 }}>
          <KpiStrip
            aria-label={`Uso nos últimos ${dias} dias`}
            items={[
              { icon: "group", value: formatarNumero(k?.usuarios ?? 0), label: "pessoas usaram", tone: "brand" },
              { icon: "schedule", value: duracao(k?.segundos ?? 0), label: "de uso no total", tone: "green" },
              { icon: "edit_note", value: formatarNumero(k?.alteracoes ?? 0), label: "alterações", tone: "yellow" },
              { icon: "block", value: formatarNumero(k?.negados ?? 0), label: "logins negados", tone: "red" },
              {
                icon: "my_location",
                value: `${formatarNumero(k?.comLocalizacao ?? 0)} de ${formatarNumero(k?.usuarios ?? 0)}`,
                label: "com localização do aparelho",
                tone: "grey",
              },
            ]}
          />
        </Box>

        <Box
          sx={{
            width: LARGURA,
            mt: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" },
            gap: 2,
          }}
        >
          <Cartao titulo="Atividade por dia" subtitulo="Pessoas que usaram o painel e alterações feitas.">
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={porDia} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F4" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="a" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="u" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="a" dataKey="alteracoes" name="Alterações" fill="#F9AB00" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="u" dataKey="usuarios" name="Pessoas" stroke="#1264C7" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Cartao>
          <Cartao titulo="Horas de uso por dia" subtitulo="Soma do tempo com o painel aberto, de todas as pessoas.">
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={porDia} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F4" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [`${v} h`, "Uso"]} />
                  <Bar dataKey="horas" name="Horas" fill="#34A853" radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Cartao>
        </Box>

        <Box sx={{ width: LARGURA, mt: 2 }}>
          <Cartao
            titulo="Quando a equipe usa o painel"
            subtitulo="Cada quadrado é uma hora do dia; quanto mais escuro, mais uso. Uso de madrugada ou fim de semana merece um olhar."
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "36px repeat(24, minmax(14px, 1fr))", gap: "3px", minWidth: 480 }}>
                <span />
                {Array.from({ length: 24 }, (_, h) => (
                  <Typography key={h} variant="caption" color="text.secondary" sx={{ textAlign: "center", fontSize: 10 }}>
                    {h % 3 === 0 ? h : ""}
                  </Typography>
                ))}
                {calor.m.map((linha, dow) => (
                  <Box key={dow} sx={{ display: "contents" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: "18px" }}>
                      {DIAS_SEMANA[dow]}
                    </Typography>
                    {linha.map((v, h) => (
                      <MuiTooltip key={h} title={`${DIAS_SEMANA[dow]} ${h}h: ${v} evento(s)`}>
                        <Box
                          sx={{
                            height: 18,
                            borderRadius: "3px",
                            bgcolor: v === 0 ? "#F1F3F4" : `rgba(18, 100, 199, ${0.15 + 0.85 * (v / Math.max(1, calor.max))})`,
                          }}
                        />
                      </MuiTooltip>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Cartao>
        </Box>

        <Box sx={{ width: LARGURA, mt: 3, marginBottom: isMobile() ? "2vh" : 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Quem usa e quanto
          </Typography>
          <AppDataGrid
            columns={colunas}
            rows={linhas}
            tableLabel="pessoas"
            resultsLabel={`${formatarNumero(painel?.usuarios.length ?? 0)} pessoas`}
            pagination={{
              page: pagina.page,
              pageSize: pagina.pageSize,
              totalCount: painel?.usuarios.length ?? 0,
              onPageChange: (page) => setPagina((p) => ({ ...p, page })),
              onPageSizeChange: (pageSize) => setPagina({ page: 1, pageSize }),
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Ninguém usou o painel no período"
            noDataDescription="O registro de uso começou em 01/10/2026."
            isLoading={carregando}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            "Redes" em vermelho: 3 ou mais IPs diferentes no período. Pode ser só celular trocando de rede; compare as cidades.
          </Typography>
        </Box>

        <Box
          sx={{
            width: LARGURA,
            mt: 3,
            mb: isMobile() ? "6vh" : 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          <Cartao titulo="Perfis mais editados">
            <Lista
              linhas={(painel?.perfis ?? []).map((p) => ({
                chave: p.profileRef,
                principal: p.profileName ?? p.profileRef,
                secundario: p.quem ?? undefined,
                valor: formatarNumero(Number(p.alteracoes)),
              }))}
            />
          </Cartao>
          <Cartao titulo="Telas mais abertas">
            <Lista
              linhas={(painel?.telas ?? []).map((t) => ({
                chave: t.tela,
                principal: t.tela.replace(/^Abriu /, ""),
                secundario: `${formatarNumero(Number(t.usuarios))} pessoa(s)`,
                valor: formatarNumero(Number(t.aberturas)),
              }))}
            />
          </Cartao>
          <Cartao titulo="De onde acessam" subtitulo="Cidade pelo IP; pessoas distintas.">
            <Lista
              linhas={(painel?.cidades ?? []).map((c) => ({
                chave: `${c.cidade}|${c.uf}|${c.pais}`,
                principal: c.cidade,
                secundario: [c.uf, c.pais && c.pais !== "BR" ? c.pais : null].filter(Boolean).join(" · ") || undefined,
                valor: formatarNumero(Number(c.usuarios)),
              }))}
            />
          </Cartao>
          <Cartao titulo="Aparelhos" subtitulo="Navegador e sistema; pessoas distintas.">
            <Lista
              linhas={aparelhosAgrupados(painel?.aparelhos ?? []).map((a) => ({
                chave: a.nome,
                principal: a.nome,
                valor: formatarNumero(a.usuarios),
              }))}
            />
          </Cartao>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ width: LARGURA, mb: 3 }}>
          Cidade pelo IP:{" "}
          <Link href="https://db-ip.com" target="_blank" rel="noopener noreferrer">
            IP Geolocation by DB-IP
          </Link>
          . Mapa © OpenStreetMap.
        </Typography>
      </Container>
    </Page.Root>
  );
};
